"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { Twitter, Linkedin, Instagram } from "lucide-react";
import { cn, containerClasses } from "@/lib/utils";

// Animation variants
const motionVariants = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  }
};

// Navigation data
interface NavLink {
  title: string;
  href: string;
}

interface SocialLink {
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  label: string;
}

const mainLinks: NavLink[] = [
  { title: "Über Uns", href: "/about-us" },
  { title: "Kandidaten", href: "/candidates" },
  { title: "Unternehmen", href: "/companies" },
];

const legalLinks: NavLink[] = [
  { title: "Kontakt", href: "/contact" },
  { title: "Datenschutz", href: "/privacy" },
  { title: "Impressum", href: "/imprint" },
];

const socialLinks: SocialLink[] = [
  { icon: Twitter, href: "/about-us", label: "Twitter" },
  { icon: Linkedin, href: "/about-us", label: "LinkedIn" },
  { icon: Instagram, href: "/about-us", label: "Instagram" },
];

interface FooterColumnProps {
  title: string;
  links: NavLink[];
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <motion.div variants={motionVariants.item} className="col-span-1 sm:col-span-3">
      <h3 className="text-h4 mb-4">
        {title}
      </h3>
      <div className="space-y-3">
        {links.map((link) => (
          <Link
            key={link.title}
            href={link.href}
            className={cn(
              "block text-body",
              "text-muted-foreground hover:text-foreground",
              "transition-colors duration-300"
            )}
          >
            {link.title}
          </Link>
        ))}
      </div>
    </motion.div>
  );
}

function SocialLinks() {
  return (
    <motion.div variants={motionVariants.item} className="col-span-2 sm:col-span-6 sm:flex sm:justify-end">
      <div>
        <h3 className="text-h4 mb-4">
          Folgen Sie uns
        </h3>
        <div className="flex gap-3">
          {socialLinks.map(({ icon: Icon, href, label }) => (
            <Link
              key={label}
              href={href}
              aria-label={label}
              className={cn(
                "w-10 h-10 sm:w-12 sm:h-12",
                "rounded-full",
                "bg-gradient-floral",
                "border border-white/40",
                "shadow-neumorphic",
                "hover:shadow-neumorphic-lg",
                "transition-all duration-300",
                "flex items-center justify-center",
                "group"
              )}
            >
              <Icon className={cn(
                "w-5 h-5 sm:w-6 sm:h-6",
                "text-muted-foreground group-hover:text-foreground",
                "transition-colors duration-300"
              )} />
            </Link>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white font-gilroy" role="contentinfo">
      <div className={containerClasses}>
        <motion.div
          variants={motionVariants.container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="py-4 sm:py-6 border-t"
        >
          {/* Main Content Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-12 gap-6 mb-4">
            <FooterColumn title="Navigation" links={mainLinks} />
            <FooterColumn title="Rechtliches" links={legalLinks} />
            <SocialLinks />
          </div>

          {/* Copyright */}
          <motion.div 
            variants={motionVariants.item}
            className={cn(
              "mt-6 md:mt-8 pt-4 md:pt-6",
              "border-t",
              "flex justify-center",
              "text-body text-muted-foreground"
            )}
          >
            <p>© {currentYear} umynd – Alle Rechte vorbehalten.</p>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
} 